import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';

const CallerVendors = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [dateRange, setDateRange] = useState('');
  const [inListFilter, setInListFilter] = useState('1');
  const [lowBalanceFilter, setLowBalanceFilter] = useState('All');

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: searchKeyword,
        date: dateRange,
        lawbalance: lowBalanceFilter
      };
      
      const response = await apiService.post('vendors', params);
      if (response && response.data) {
        setVendors(response.data);
        setTotalRecords(response.recordsTotal || 0);
      }
    } catch (error) {
      toast.error('Failed to load vendors');
      console.error('Vendors fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, searchKeyword, dateRange, lowBalanceFilter]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchVendors();
    }, searchKeyword ? 500 : 0);
    
    return () => clearTimeout(timeoutId);
  }, [fetchVendors]);

  const handleSearch = (value) => {
    setSearchKeyword(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'inList') {
      setInListFilter(value);
    } else if (filterType === 'lowBalance') {
      setLowBalanceFilter(value);
    } else if (filterType === 'dateRange') {
      setDateRange(value);
    }
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-0">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Header */}
          <div className="bg-gray-50 p-6 border-b border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-2xl font-bold text-gray-900">Vendors</h1>
            </div>
            
            {/* Filters */}
            <div className="flex flex-wrap gap-4 items-center">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <i className="bi bi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  placeholder="Type Name, Phone or Email"
                  value={searchKeyword}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                />
              </div>

              {/* Date Range */}
              <div className="flex-1 min-w-[150px]">
                <input
                  type="text"
                  placeholder="Pick date range"
                  value={dateRange}
                  onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                />
              </div>

              {/* In List Filter - Only for admin */}
              {user?.role === 'admin' && (
                <div className="flex-1 min-w-[120px]">
                  <select
                    value={inListFilter}
                    onChange={(e) => handleFilterChange('inList', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7] bg-white appearance-none cursor-pointer"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: 'right 0.5rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.5em 1.5em',
                      paddingRight: '2.5rem'
                    }}
                  >
                    <option value="1">All</option>
                    <option value="yes">In List</option>
                    <option value="no">Not In List</option>
                  </select>
                </div>
              )}

              {/* Low Balance Filter */}
              <div className="flex-1 min-w-[140px]">
                <select
                  value={lowBalanceFilter}
                  onChange={(e) => handleFilterChange('lowBalance', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7] bg-white appearance-none cursor-pointer"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.5rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.5em 1.5em',
                    paddingRight: '2.5rem'
                  }}
                >
                  <option value="">Low Balance</option>
                  <option value="All">All</option>
                  <option value="lawbalance">Low Balance</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">View</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#009ef7]"></div>
                      </div>
                    </td>
                  </tr>
                ) : vendors.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                      No vendors found
                    </td>
                  </tr>
                ) : (
                  vendors.map((vendor, index) => (
                    <tr key={vendor._id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {((currentPage - 1) * recordsPerPage) + index + 1}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="w-16 h-16">
                          {vendor.image ? (
                            <img 
                              src={`${process.env.REACT_APP_MEDIA_URL}${vendor.image}`} 
                              alt="Vendor" 
                              className="w-full h-full object-cover rounded"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-200 rounded flex items-center justify-center">
                              <i className="bi bi-person text-gray-400 text-xl"></i>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex flex-col items-center gap-2">
                          <span className="px-3 py-1 bg-[#009ef7]/10 text-[#009ef7] text-xs font-semibold rounded">
                            {vendor.business_name}
                          </span>
                          <a 
                            href={`https://www.travellinglark.com/profile/${vendor.slug}/${vendor.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1 bg-cyan-100 text-cyan-800 text-xs font-semibold rounded flex items-center hover:bg-cyan-200"
                          >
                            <i className="bi bi-building mr-1"></i>
                            Business Profile
                          </a>
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {vendor.first_name}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex flex-col">
                          <span>{vendor.email || '-'}</span>
                          <span className="text-gray-500">{vendor.phone || '-'}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {vendor.address || '--'}
                        {vendor.stateId?.name && `, ${vendor.stateId.name}`}
                        {vendor.countryId?.name && `, ${vendor.countryId.name}`}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {new Date(vendor.createdAt).toLocaleString('en-IN', {
                          timeZone: 'Asia/Kolkata'
                        })}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-center">
                        <div className="flex flex-col items-center gap-2">
                          <button
                            onClick={() => navigate(`/vendorSubscriptionList/${vendor._id}`)}
                            className="px-3 py-1 bg-[#009ef7]/10 text-blue-700 text-xs font-semibold rounded hover:bg-blue-200 flex items-center w-full justify-start"
                          >
                            <i className="bi bi-cart mr-2"></i>
                            Transaction
                          </button>
                          <button
                            onClick={() => navigate(`/vendorWallet/${vendor._id}`)}
                            className="px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded hover:bg-red-200 flex items-center w-full justify-start"
                          >
                            <i className="bi bi-wallet mr-2"></i>
                            Wallet ({vendor.walletBalance || '0'})
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm text-gray-700">
                  Showing {((currentPage - 1) * recordsPerPage) + 1} to {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords} results
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallerVendors;